import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { CycleSchemePay, InfoOutline } from '@yme/assets/icons';
import { CONTAINED_VARIANT, NO_ICON, SIZE_44 } from '@yme/atoms/button';
import { Checkbox } from '@yme/atoms/checkbox';
import { ICON_SIZE, SVGIcon } from '@yme/atoms/svg-icon';
import { getIsCartConvertedToOrder } from '@yme/common/redux/cart/selectors';
import { setCertificateCodeValidationResult, setHasValidationError, } from '@yme/components/CheckoutForm/PaymentForm/PaymentOptions/redux/actions';
import { getCertificateCode, getHasValidationError, getIsCertificateValidating, getValidationResult, } from '@yme/components/CheckoutForm/PaymentForm/PaymentOptions/redux/selectors';
import { checkboxPaymentState } from '@yme/components/CheckoutForm/PaymentForm/constants';
import { setPaymentProvider } from '@yme/components/CheckoutForm/redux/actions';
import { FormFieldType } from '@yme/molecules/form-field';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { regExpCertificateCode } from '@yme/utils/regEx';
import { RadioButtonStyled, DescriptionStyled } from '../../styled';
import { CycleSchemeAlert } from '../CycleSchemeAlert';
import { TransformedTextToReactNode } from '../TransformedTextToReactNode';
import { ButtonStyled, FormStyled, CertificateCodeInputStyled, PaymentRowStyled, CERTIFICATE_CODES, ALERT_TYPE, VALIDATION_SCHEME_MESSAGE, CYCLE_SCHEME_DESCRIPTION, CODES, CERTIFICATE_STATUS, TEXT_CERTIFICATE_AGREEMENT, CERTIFICATE_CODE, CYCLE_SCHEME_VALUE, CERTIFICATE_INPUT_PLACEHOLDER, CERTIFICATE_INPUT_LABEL, } from './';
export const CycleSchemeOption = ({ isMobileView, inputValue, isRightToLeft, onChange, optionName = CYCLE_SCHEME_VALUE, onSubmit, }) => {
    const dispatch = useDispatch();
    const formikRef = useRef(null);
    const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
    const isCertificateValidating = useSelector(getIsCertificateValidating);
    const isCartConvertedToOrder = useSelector(getIsCartConvertedToOrder);
    const validationResult = useSelector(getValidationResult);
    const certificateCode = useSelector(getCertificateCode);
    const hasValidationError = useSelector(getHasValidationError);
    const initialValues = {
        [CERTIFICATE_CODE]: '',
    };
    const isVisibleAlert = validationResult && CODES.includes(validationResult);
    const isCertificateExceed = validationResult === CERTIFICATE_STATUS.exceed;
    const isCertificateInvalid = !isCertificateValidating && validationResult === CERTIFICATE_STATUS.invalid;
    const handleChange = useCallback(() => {
        if (formikRef.current && formikRef.current.errors.certificateCode) {
            dispatch(setCertificateCodeValidationResult(''));
            dispatch(setHasValidationError(true));
        }
    }, [dispatch]);
    const onHandleChangeCheckbox = (event) => {
        event.persist();
        const { value } = event?.target;
        onChange(value);
    };
    useEffect(() => {
        const isCycleSchemeValue = localStorage.getItem(checkboxPaymentState) === CYCLE_SCHEME_VALUE;
        if (isCycleSchemeValue) {
            onChange(CYCLE_SCHEME_VALUE);
            dispatch(setPaymentProvider(CYCLE_SCHEME_VALUE));
        }
    }, [dispatch, onChange]);
    useEffect(() => {
        if (inputValue === optionName && !certificateCode) {
            dispatch(setHasValidationError(true));
        }
        else if (formikRef.current &&
            !formikRef.current.errors.certificateCode?.trim() &&
            inputValue !== optionName) {
            dispatch(setHasValidationError(false));
        }
    }, [inputValue, certificateCode, optionName, dispatch]);
    useEffect(() => {
        if (isCertificateInvalid && formikRef.current) {
            formikRef.current.setErrors({
                ...formikRef.current.errors,
                [CERTIFICATE_CODE]: CERTIFICATE_CODES[CERTIFICATE_STATUS.invalid],
            });
        }
    }, [isCertificateInvalid]);
    const handleSubmit = useCallback((values) => {
        if (values.certificateCode.trim()) {
            onSubmit?.(values.certificateCode);
        }
    }, [onSubmit]);
    const validationSchema = Yup.object().shape({
        [CERTIFICATE_CODE]: Yup.string()
            .trim()
            .required(' ')
            .matches(regExpCertificateCode, VALIDATION_SCHEME_MESSAGE.wrongFormat),
    });
    const onUndo = () => {
        dispatch(setCertificateCodeValidationResult(''));
        dispatch(setHasValidationError(true));
        setIsCheckboxChecked(false);
    };
    const onChangeCheckbox = useCallback((evt) => {
        dispatch(setHasValidationError(!evt.target.checked));
        setIsCheckboxChecked(evt.target.checked);
    }, [dispatch]);
    const handledRenderMessageCallback = useCallback((value) => {
        const containsEmailPlaceholder = /\{email}/.test(value);
        if (containsEmailPlaceholder) {
            return React.createElement(TransformedTextToReactNode, { text: value });
        }
        return value;
    }, []);
    useEffect(() => {
        if (isCertificateExceed && !isCheckboxChecked && !hasValidationError) {
            dispatch(setHasValidationError(true));
        }
    }, [dispatch, hasValidationError, isCertificateExceed, isCheckboxChecked]);
    return (React.createElement(Formik, { enaleReinitialize: true, validateOnBlur: true, validateOnMount: true, validateOnChange: true, initialValues: initialValues, validationSchema: validationSchema, onSubmit: handleSubmit, innerRef: formikRef }, ({ errors }) => (React.createElement(FlexWrapper, { flexDirection: "row", alignItems: "center" },
        React.createElement(FormStyled, null,
            React.createElement(RadioButtonStyled, { isMobileView: isMobileView, className: inputValue === optionName ? 'active-last-payment' : '', key: optionName, id: optionName, value: optionName, name: "payment-option", label: "Cyclescheme", cornerText: React.createElement(DescriptionStyled, null, CYCLE_SCHEME_DESCRIPTION), innerNode: React.createElement(SVGIcon, { type: CycleSchemePay, size: ICON_SIZE.s }), isChecked: inputValue === optionName, isRightToLeft: isRightToLeft, onChange: onHandleChangeCheckbox }),
            inputValue === optionName && (React.createElement(PaymentRowStyled, { isActive: inputValue === optionName, isVisibleAlert: isVisibleAlert }, isVisibleAlert ? (React.createElement(FlexWrapper, { flexDirection: "column", gap: "16px" },
                React.createElement(CycleSchemeAlert, { isRightToLeft: isRightToLeft, alertType: ALERT_TYPE[validationResult], message: CERTIFICATE_CODES[validationResult], onHandleClick: onUndo }, isCertificateExceed && (React.createElement(Checkbox, { checked: isCheckboxChecked, isRightToLeft: isRightToLeft, value: TEXT_CERTIFICATE_AGREEMENT, onChange: onChangeCheckbox, id: "certificateAggrementId", required: true }))))) : (React.createElement(React.Fragment, null,
                React.createElement(CertificateCodeInputStyled, { fieldType: FormFieldType.INPUT, id: CERTIFICATE_CODE, name: CERTIFICATE_CODE, rightIcon: InfoOutline, isRequired: inputValue === optionName, isDisabled: isCartConvertedToOrder || isCertificateValidating, isRightToLeft: isRightToLeft, onChange: handleChange, renderMessageCallback: handledRenderMessageCallback, label: CERTIFICATE_INPUT_LABEL, placeholder: CERTIFICATE_INPUT_PLACEHOLDER, dataTestId: "certificate-code-input" }),
                React.createElement(ButtonStyled, { width: "auto", type: "submit", disabled: isCertificateInvalid
                        ? !Boolean(errors.certificateCode)
                        : Boolean(errors.certificateCode) || isCertificateValidating, label: "Check", buttonVariant: CONTAINED_VARIANT, hasIconButton: NO_ICON, size: SIZE_44 }))))))))));
};
